import { ChangeEvent, FC, useEffect, useState } from 'react';

import postalCodeRegex from '@eneco/common/validation/postalCodeRegex';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Box, Grid, InputSelect, InputText, Stack, VisuallyHidden } from '@eneco/sparky';
import { useMachine } from '@xstate/react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

import { Layout, NextButton } from '../../components';
import { StepHeader } from '../../components/StepHeader/StepHeader';
import { useStepContent } from '../../hooks';
import { addressMachine } from '../../machines';
import { FlowError } from '../../stepsSharedComponents/Error/Error';
import { FlowContext } from '../../types';
import type { FlowNotificationContent } from '../../types/content';
import { useFlowHooks } from '../../utils/FlowProvider';
import { readSectionField } from '../../utils/readSectionField';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
}

export const AddressStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const street = useFlowSelector(state => state.context.street);
  const city = useFlowSelector(state => state.context.city);

  const [addressState, sendToAddressMachine] = useMachine(addressMachine);

  const content = useStepContent('AddressStep');

  const formSchema = yup.object({
    postalCode: yup
      .string()
      .required(content?.postalCodeFormField?.requiredMessage)
      .matches(postalCodeRegex, content?.postalCodeFormField?.validationMessage),
    houseNumber: yup
      .number()
      .required(content?.houseNumberFormField?.requiredMessage)
      .typeError(content?.houseNumberFormField?.validationMessage ?? ''),
    houseNumberSuffix: yup.string().optional(),

    street: yup.string().required(),
    city: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    getFieldState,
    getValues,
    setValue,
    formState: { errors, isSubmitSuccessful, isValidating },
  } = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      postalCode,
      houseNumber,
      houseNumberSuffix: houseNumberSuffix ?? undefined,
      street,
      city,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { businessUnit, label } = useDC();
  const { address } = useFormatter();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [hasFormValidationErrors, setHasFormValidationErrors] = useState(false);

  const [postCodeToValidate, setPostCodeToValidate] = useState(postalCode);
  const [houseNumberToValidate, setHouseNumberToValidate] = useState(houseNumber);
  const [houseNumberSuffixToValidate, setHouseNumberSuffixToValidate] = useState(houseNumberSuffix);

  const [shouldDisableHouseNumberSuffix, setShouldDisableHouseNumberSuffix] = useState(
    !houseNumberSuffix || !!addressState.context.houseNumberSuffixes,
  );

  useDebounce(
    () => {
      if (postCodeToValidate && houseNumberToValidate) {
        sendToAddressMachine({
          type: 'GET_ADDRESS',
          config: {
            businessUnit,
            label,
          },
          values: {
            postalCode: postCodeToValidate,
            houseNumber: houseNumberToValidate,
          },
        });
      }
    },
    500,
    [postCodeToValidate, houseNumberToValidate],
  );

  useEffect(() => {
    setIsLoading(addressState.matches('FETCHING'));
    setIsSuccessful(addressState.matches('SUCCESS'));
    setIsNotFound(addressState.matches('NOT_FOUND'));
    setIsFailed(addressState.matches('ERROR') || addressState.matches('NOT_FOUND'));

    setShouldDisableHouseNumberSuffix(!addressState.matches('SUCCESS'));

    if (addressState.matches('SUCCESS')) {
      const { street, city } = addressState.context;

      // set values of the hidden fields
      setValue('city', city ?? '');
      setValue('street', street ?? '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);

  useEffect(() => {
    if (isSubmitSuccessful && isSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...formData,
          houseNumber: Number(formData.houseNumber),
          houseNumberSuffix: formData.houseNumberSuffix || undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    const { postalCode, houseNumber, houseNumberSuffix } = getValues();

    setHasFormValidationErrors(!(!errors.houseNumber && !errors.postalCode));
    setHouseNumberSuffixToValidate(houseNumberSuffix);

    if (!isValidating && postalCode && houseNumber) {
      if (getFieldState('postalCode').isTouched || getFieldState('houseNumber').isTouched) {
        if (!getFieldState('postalCode').error && !getFieldState('houseNumber').error) {
          if (postalCode !== postCodeToValidate || houseNumber !== houseNumberToValidate) {
            setShouldDisableHouseNumberSuffix(true);

            // reset values of the form fields
            setValue('houseNumberSuffix', '');
            setValue('city', '');
            setValue('street', '');

            // due to use of a "debounce" we update the state right away
            setIsSuccessful(false);
            setIsLoading(true);
            setIsFailed(false);

            setHouseNumberSuffixToValidate('');
          }

          setPostCodeToValidate(postalCode);
          setHouseNumberToValidate(houseNumber);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidating]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader
        title={content?.title}
        text={content?.text}
        notification={readSectionField<FlowNotificationContent>(content, 'content', 'notification')}
      />
      <Stack.Item as="fieldset">
        <VisuallyHidden>
          <legend>adres invoer</legend>
        </VisuallyHidden>
        <Grid columns="3" columnGap="3" gridTemplateColumns="2fr 2fr 1.6fr">
          <InputText
            {...register('postalCode', {
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                const target = event.target;
                const position = target.selectionStart;

                target.value = target?.value?.toUpperCase();
                target.selectionEnd = position;
              },
            })}
            error={errors.postalCode?.message}
            label={content?.postalCodeFormField?.label ?? ''}
            placeholder={content?.postalCodeFormField?.placeholder}
            hint={content?.postalCodeFormField?.hint}
          />

          <InputText
            {...register('houseNumber')}
            error={errors.houseNumber?.message}
            label={content?.houseNumberFormField?.label ?? ''}
            placeholder={content?.houseNumberFormField?.placeholder}
            hint={content?.houseNumberFormField?.hint}
          />

          <InputSelect
            {...register('houseNumberSuffix')}
            label={content?.houseNumberSuffixFormField?.label ?? ''}
            placeholder=""
            hint={content?.houseNumberSuffixFormField?.hint}
            isDisabled={hasFormValidationErrors || shouldDisableHouseNumberSuffix}
            options={[
              { label: '-', value: '' },
              ...(addressState?.context?.houseNumberSuffixes ?? []).map(suffix => {
                return {
                  label: suffix,
                  value: suffix,
                };
              }),
            ]}
          />
        </Grid>

        {!isLoading && !hasFormValidationErrors && (
          <>
            {isSuccessful && (
              <Box paddingTop="2">
                {address.long({
                  street: addressState.context.street,
                  houseNumber: addressState.context.houseNumber,
                  houseNumberSuffix: houseNumberSuffixToValidate,
                  city: addressState.context.city,
                  postalCode: addressState.context.postalCode,
                })}
              </Box>
            )}

            {isFailed && (
              <Box paddingTop="4">
                <FlowError
                  textualData={{
                    notification: isNotFound ? content?.notFoundErrorNotification : content?.errorNotification,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Stack.Item>

      <input {...register('city')} type="hidden" />
      <input {...register('street')} type="hidden" />

      <NextButton isLoading={isSubmitSuccessful || isLoading}>{content?.nextStepText}</NextButton>
    </Layout.Content>
  );
};
